import { Link } from 'react-router-dom';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between border-b border-slate-700/50">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg shadow-green-500/25">
            <span className="text-white text-xl">✓</span>
          </div>
          <span className="text-xl font-bold text-white">
            Demo<span className="text-green-400">Path</span>
          </span>
        </Link>
        <Link
          to="/"
          className="text-slate-400 hover:text-white transition-colors text-sm"
        >
          ← Back to Home
        </Link>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy & Data Usage</h1>
        <p className="text-slate-400 mb-8">Last updated: January 2026</p>

        <div className="space-y-10 text-slate-300">
          {/* Overview */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Overview</h2>
            <p className="mb-4">
              DemoPath is a real-time speech recognition tool that helps you track talking points during demos and presentations.
              We take your privacy seriously and are committed to being transparent about how we handle your data.
            </p>
            <p className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-green-400">
              <strong>The short version:</strong> We collect only what's necessary to provide the service,
              we don't store your audio, and we never sell your data to anyone.
            </p>
          </section>

          {/* Our Commitment */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Our Commitment to Your Privacy</h2>
            <p>
              <strong className="text-white">We do not sell your personal information.</strong> Period.
              We will never sell, rent, or share your personal data with data brokers, advertisers, or any
              third parties for their marketing purposes. The only third parties who receive your data are
              the service providers listed below, and only to the extent necessary to provide DemoPath's functionality.
            </p>
          </section>

          {/* What Data We Collect */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">What Data We Collect</h2>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">Account Information</h3>
            <p>
              When you create an account, we collect your email address and name through our authentication
              provider (Clerk). This information is used to identify your account and provide personalized features.
            </p>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">Audio Data</h3>
            <p className="mb-3">
              When you use DemoPath, audio from your microphone is streamed directly to our speech recognition
              provider (Deepgram) for real-time transcription.
            </p>
            <p className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-green-300">
              <strong>Important:</strong> We do not store your audio recordings. The audio stream is processed
              in real-time and immediately discarded after transcription. Neither DemoPath nor Deepgram retains your audio data.
            </p>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">Transcription Data</h3>
            <p className="mb-3">
              Depending on your subscription tier, we may store session transcripts to provide features like
              session history, analytics, and team collaboration:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong className="text-white">Free tier:</strong> Transcripts processed in real-time only — not stored</li>
              <li><strong className="text-white">Individual tier:</strong> Transcripts processed in real-time only — not stored</li>
              <li><strong className="text-white">Team tier:</strong> Transcripts stored for up to 1 year to enable session history and analytics</li>
            </ul>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">Agenda Content</h3>
            <p className="mb-3">
              The talking points and agenda items you enter are processed to provide the matching functionality.
              Team tier users may have agenda templates stored for reuse.
            </p>
            <p>
              When you use the optional AI agenda expansion feature, your agenda text is sent to Anthropic's Claude API
              for processing. This data is processed according to Anthropic's API data usage policy and is not used to
              train their AI models. You can use DemoPath without this feature if you prefer not to share agenda content with Anthropic.
            </p>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">Usage Data</h3>
            <p className="mb-3">We collect anonymized, aggregated usage statistics to improve our service, including:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Session duration and timing</li>
              <li>Feature usage patterns</li>
              <li>Matching accuracy metrics (e.g., which keywords matched successfully)</li>
            </ul>
            <p className="mt-3">
              This data is stripped of personally identifiable information and cannot be traced back to individual users or sessions.
            </p>
          </section>

          {/* How We Use Your Data */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">How We Use Your Data</h2>
            <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
              <li>To provide the core DemoPath service (speech recognition and talking point tracking)</li>
              <li>To authenticate you and manage your account</li>
              <li>To store session history and templates (Team tier only)</li>
              <li>To improve our matching algorithms using anonymized, aggregated data</li>
              <li>To communicate important service updates</li>
              <li>To process payments (for paid tiers)</li>
              <li>To respond to support requests</li>
            </ul>

            <p className="font-medium text-white mb-2">We do not use your data for:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-slate-400">
              <li>Advertising or marketing by third parties</li>
              <li>Sale to data brokers</li>
              <li>Building profiles for purposes unrelated to DemoPath</li>
              <li>Training AI models (your transcripts and agendas are not used for model training)</li>
            </ul>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Third-Party Services</h2>
            <p className="mb-4">
              We use the following third-party services to provide DemoPath. Each receives only the minimum data necessary for their function:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-700 rounded-lg overflow-hidden">
                <thead className="bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-white font-medium">Service</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Purpose</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Data Shared</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  <tr className="bg-slate-800/50">
                    <td className="px-4 py-3 font-medium text-white">Clerk</td>
                    <td className="px-4 py-3">Authentication and user management</td>
                    <td className="px-4 py-3">Email, name</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-white">Deepgram</td>
                    <td className="px-4 py-3">Real-time speech-to-text</td>
                    <td className="px-4 py-3">Audio stream (not retained)</td>
                  </tr>
                  <tr className="bg-slate-800/50">
                    <td className="px-4 py-3 font-medium text-white">Anthropic</td>
                    <td className="px-4 py-3">AI-powered agenda expansion (optional)</td>
                    <td className="px-4 py-3">Agenda text only</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-white">Stripe</td>
                    <td className="px-4 py-3">Payment processing</td>
                    <td className="px-4 py-3">Payment details (for paid tiers)</td>
                  </tr>
                  <tr className="bg-slate-800/50">
                    <td className="px-4 py-3 font-medium text-white">AWS</td>
                    <td className="px-4 py-3">Cloud infrastructure hosting</td>
                    <td className="px-4 py-3">All data (encrypted)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm">
              Each of these services operates under their own privacy policy. We have selected providers with strong
              privacy practices and appropriate data handling agreements.
            </p>
          </section>

          {/* Cookies and Tracking */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Cookies and Tracking</h2>
            <p className="mb-3">DemoPath uses essential cookies to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
              <li>Maintain your session and authentication state</li>
              <li>Remember your preferences</li>
              <li>Ensure the application functions correctly</li>
            </ul>
            <p className="font-medium text-white mb-2">We do not use:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-slate-400">
              <li>Third-party advertising cookies</li>
              <li>Cross-site tracking cookies</li>
              <li>Analytics that identify individual users</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Data Retention</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-700 rounded-lg overflow-hidden">
                <thead className="bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-white font-medium">Tier</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Session Data</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Transcripts</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Account Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  <tr className="bg-slate-800/50">
                    <td className="px-4 py-3 font-medium text-white">Free</td>
                    <td className="px-4 py-3">Not retained</td>
                    <td className="px-4 py-3">Not retained</td>
                    <td className="px-4 py-3">Until account deletion</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-white">Individual</td>
                    <td className="px-4 py-3">Not retained</td>
                    <td className="px-4 py-3">Not retained</td>
                    <td className="px-4 py-3">Until account deletion</td>
                  </tr>
                  <tr className="bg-slate-800/50">
                    <td className="px-4 py-3 font-medium text-white">Team</td>
                    <td className="px-4 py-3">1 year</td>
                    <td className="px-4 py-3">1 year</td>
                    <td className="px-4 py-3">Until account deletion</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4">
              You can request deletion of your account and all associated data at any time by contacting us at{' '}
              <a href="mailto:privacy@demopath.ai" className="text-green-400 hover:text-green-300">privacy@demopath.ai</a>.
              We will process deletion requests within 30 days.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Data Security</h2>
            <p className="mb-3">We implement industry-standard security measures including:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">Encryption in transit:</strong> All data transmission uses TLS/SSL encryption</li>
              <li><strong className="text-white">Encryption at rest:</strong> Stored data is encrypted using AES-256</li>
              <li><strong className="text-white">Secure authentication:</strong> Managed by Clerk with support for multi-factor authentication</li>
              <li><strong className="text-white">Access controls:</strong> Limited employee access to production data</li>
              <li><strong className="text-white">Infrastructure security:</strong> Hosted on AWS with SOC 2 compliant infrastructure</li>
              <li><strong className="text-white">Regular updates:</strong> Security patches and updates applied promptly</li>
            </ul>
            <p className="mt-4 text-sm text-slate-400">
              While we take extensive measures to protect your data, no system is 100% secure. We will notify
              affected users promptly in the unlikely event of a data breach.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Your Rights</h2>
            <p className="mb-3">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">Access</strong> your personal data — request a copy of the data we hold about you</li>
              <li><strong className="text-white">Correct</strong> inaccurate data — update your profile or request corrections</li>
              <li><strong className="text-white">Delete</strong> your data — request deletion of your account and all associated data</li>
              <li><strong className="text-white">Export</strong> your data — receive your data in a portable format (JSON/CSV)</li>
              <li><strong className="text-white">Opt out</strong> of optional data collection — disable AI features or anonymized analytics</li>
              <li><strong className="text-white">Withdraw consent</strong> — for any processing based on consent</li>
            </ul>
            <p className="mt-4">
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:privacy@demopath.ai" className="text-green-400 hover:text-green-300">privacy@demopath.ai</a>.
              We will respond within 30 days.
            </p>
          </section>

          {/* California Privacy Rights (CCPA) */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">California Privacy Rights (CCPA)</h2>
            <p className="mb-3">If you are a California resident, you have additional rights under the California Consumer Privacy Act:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
              <li><strong className="text-white">Right to know</strong> what personal information we collect, use, and disclose</li>
              <li><strong className="text-white">Right to delete</strong> your personal information</li>
              <li><strong className="text-white">Right to opt-out</strong> of the sale of personal information</li>
              <li><strong className="text-white">Right to non-discrimination</strong> for exercising your privacy rights</li>
            </ul>
            <p className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <strong className="text-white">We do not sell your personal information.</strong> Because we do not sell personal
              information, there is no need to opt out of such sales.
            </p>
            <p className="mt-4">
              To submit a CCPA request, contact us at{' '}
              <a href="mailto:privacy@demopath.ai" className="text-green-400 hover:text-green-300">privacy@demopath.ai</a>.
            </p>
          </section>

          {/* European Privacy Rights (GDPR) */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">European Privacy Rights (GDPR)</h2>
            <p className="mb-4">
              If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you have additional
              rights under the General Data Protection Regulation:
            </p>

            <h3 className="text-lg font-medium text-white mb-2">Legal basis for processing:</h3>
            <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
              <li><strong className="text-white">Contract:</strong> Processing necessary to provide the DemoPath service you requested</li>
              <li><strong className="text-white">Legitimate interest:</strong> Analytics and service improvement using anonymized data</li>
              <li><strong className="text-white">Consent:</strong> Optional features like AI agenda expansion</li>
            </ul>

            <h3 className="text-lg font-medium text-white mb-2">Additional rights:</h3>
            <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
            </ul>

            <p className="text-sm text-slate-400">
              <strong className="text-slate-300">Data transfers:</strong> Your data may be transferred to and processed in the
              United States, where our servers are located. We ensure appropriate safeguards are in place for such transfers.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Children's Privacy</h2>
            <p className="mb-3">
              DemoPath is not intended for users under the age of 18. We do not knowingly collect personal information
              from children. If we become aware that we have collected personal information from a child under 18,
              we will take steps to delete that information promptly.
            </p>
            <p>
              If you believe a child has provided us with personal information, please contact us at{' '}
              <a href="mailto:privacy@demopath.ai" className="text-green-400 hover:text-green-300">privacy@demopath.ai</a>.
            </p>
          </section>

          {/* Beta Service Notice */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Beta Service Notice</h2>
            <p className="mb-3">DemoPath is currently in beta. This means:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Features may change without notice</li>
              <li>Data formats may be updated, requiring migration</li>
              <li>In rare cases, data may need to be reset</li>
            </ul>
            <p className="mt-3">We will provide advance notice whenever possible for any changes that affect your data.</p>
          </section>

          {/* International Data Transfers */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">International Data Transfers</h2>
            <p className="mb-3">
              DemoPath is operated from the United States. If you are accessing DemoPath from outside the United States,
              please be aware that your data may be transferred to, stored, and processed in the United States.
            </p>
            <p>
              By using DemoPath, you consent to the transfer of your data to the United States. We ensure that any such
              transfers comply with applicable data protection laws and that your data remains protected.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Changes to This Policy</h2>
            <p className="mb-3">We may update this privacy policy from time to time. When we make changes:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>We will update the "Last updated" date at the top</li>
              <li>For material changes, we will notify you via email or prominent notice in the application</li>
              <li>Your continued use of DemoPath after changes constitutes acceptance of the updated policy</li>
            </ul>
            <p className="mt-3">We encourage you to review this policy periodically.</p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Governing Law</h2>
            <p>
              This privacy policy is governed by and construed in accordance with the laws of the State of Delaware,
              United States, without regard to its conflict of law provisions.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Contact Us</h2>
            <p className="mb-4">
              If you have questions, concerns, or requests regarding this privacy policy or how we handle your data, please contact us at:
            </p>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <p className="mb-2">
                <strong className="text-white">Email:</strong>{' '}
                <a href="mailto:privacy@demopath.ai" className="text-green-400 hover:text-green-300">privacy@demopath.ai</a>
              </p>
              <p>
                <strong className="text-white">Mailing Address:</strong><br />
                Apparently Inc.
              </p>
            </div>
            <p className="mt-4 text-sm text-slate-400">We aim to respond to all inquiries within 30 days.</p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-6 border-t border-slate-700/50 mt-12">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © 2026 DemoPath by Apparently Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link
              to="/"
              className="text-slate-500 hover:text-slate-300 transition-colors"
            >
              Home
            </Link>
            <a
              href="mailto:privacy@demopath.ai"
              className="text-slate-500 hover:text-slate-300 transition-colors"
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
